import { NotificationHandler } from './notification-handler';
import { OfferHubScreen } from './offer-hub';
import type { NavigatorScreenParams } from '@react-navigation/native';
import { isLoggedInAtom, isStartingAtom } from 'app/atoms';
import { FullWidthContainerView } from 'app/components/full-width-container-view';
import { presentPayWallAtom } from 'app/atoms/ui';
import {
  createDefaultNativeStackNavigator,
  createModalScreenOptions,
  createWebModalScreenOptions,
  createWebRightPanelScreenOptions,
} from 'app/lib/navigation';
import { type Address } from 'app/types/address';
import type { ChatUser } from 'app/types/chat';
import { FreeSubScreen } from 'app/screens/free-sub';
import * as management from 'app/screens/management/navigator';
import { NotificationWarnModal } from 'app/screens/notification-warn';
import * as onboarding from 'app/screens/onboarding/navigator';
import * as passwordRecovery from 'app/screens/password-recovery/navigator';
import { PaymentScreen } from 'app/screens/profile/payment';
import { PaymentSuccessScreen } from 'app/screens/profile/payment-success';
import { PaywallScreen } from 'app/screens/paywall';
import { NotificationsSettingScreen } from 'app/screens/profile/my-preferences/notifications';
import * as quickNew from 'app/screens/quick-new/navigator';
import { SplashScreen } from 'app/screens/splash';
import type { NavigationScreen } from 'app/types/navigation';
import { isGuestAccessEnabled, useUISize } from 'app/utils/web-helpers';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { type ComponentProps, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import * as chat from './chat/navigator';
import { InviteScreen } from './invite';
import * as main from './main-tab';
import { NewScreen, type NewScreenParams } from './new';
import { BulkNewScreen } from './new/bulk';
import { NotificationExplainerScreen } from './notification-explainer';
import { TutorialModalScreen } from 'app/screens/tutorial-modal';
import type { Tutorial } from 'app/types/tutorial';
import type { FreeSubSignalData } from 'app/network/signals';
import { TrialPromoScreen } from 'app/screens/trial-promo';
import { OfferAvailabilityScreen } from 'app/screens/management/offer-availability';
import { RefrainOfferAvailabilityPopupScreen } from 'app/screens/management/refrain-offer-availability-popup';
import * as billboard from './billboard/navigator';
import * as introModal from './intro/navigator';
import { ServiceCategoriesScreen } from 'app/screens/new/service-categories';
import { CategoriesScreen } from 'app/screens/new/categories';
import { TagsScreen } from 'app/screens/new/tags';
import { NotificationSuperChangedScreen } from 'app/screens/notification-super-changed';
import { IntroScreen } from 'app/screens/onboarding/intro';
import type { Listing, NewListingSource } from 'app/types/listing';
import { ChooseUserTypeScreen } from 'app/screens/onboarding/choose-user-type';
import { PublicUserWelcomeScreen } from 'app/screens/onboarding/public-user-welcome';
import { LoanFeeScreen } from './management/loan-fee';
import { MyAddressScreen } from './profile/my-address';
import { PresentPartageClubScreen } from './intro/present-partage-club';
import { PresentCommunityScreen } from './intro/present-community';
import { NewPostScreen } from './shared/new-post';
import { PostCommentsScreen } from './shared/post-comments';
import { NewRatingScreen } from './shared/new-rating';
import { RatingThanksScreen } from './shared/rating-thanks';
import { UpdateRatingScreen } from './shared/update-rating';
import { EditPostScreen } from './shared/edit-post';
import { EditListingScreen } from './shared/edit-listing';
import { LoanDatesPickerScreen } from './shared/loan-dates-picker';
import { LoanDetailsScreen } from './management/one-loan';
import { type Loan } from 'app/types/loan';
import { StreakScreenStandalone } from './profile/streak';
import { StreakMembersScreen } from './profile/streak/streak-members-screen';
import { InsuredUserDetails } from 'app/components/insured-users';
import { getCurrentRoutePath, navigationRef, subscribeNavigationState } from 'app/lib/root-navigation';
import { HelpScreen } from './help/help-screen';
import { HowItWorksScreen } from './chat/one-channel/components/how-it-works-card';
import { NotificationsOverlayScreen } from './notifications/all-notifications';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { getUnauthenticatedWebRedirect } from './unauthenticated-web-route-guard';

export { navigationRef } from 'app/lib/root-navigation';

export type Parameters = {
  'password-recovery': NavigatorScreenParams<passwordRecovery.Parameters> | undefined;
  chat: NavigatorScreenParams<chat.Parameters> | undefined;
  invite: undefined;
  main: NavigatorScreenParams<main.Parameters> | undefined;
  new: NewScreenParams | undefined;
  bulkNew: { sessionId?: string; newListingSource?: NewListingSource } | undefined;
  'offer-hub': NewScreenParams | undefined;
  'edit-listing': {
    id: Listing['id'];
    newServiceCategoryId?: string;
    newCategoryId?: string;
    newAddress?: Listing['address'];
  };
  paywall: undefined;
  serviceCategories?: {
    goBackTo?: 'new' | 'one-listing' | 'edit-listing' | 'set-atom-then-close';
    listingId?: string;
    selectedCategory?: string;
  };
  categories: {
    goBackTo: 'new' | 'one-listing' | 'edit-listing' | 'set-atom-then-close';
    listingId?: string;
    selectedCategory?: string;
  };
  tags: {
    goBackTo: 'set-atom-then-close';
    selectedTag?: string;
  };
  'my-address': {
    id?: Address['id'];
    address?: Listing['address'] | null;
    goBackTo?: 'new' | 'one-listing' | 'edit-listing';
    listingId?: Listing['id'];
  };
  'notification-explainer': { context: 'chat'; interlocutor?: ChatUser } | { context: 'listing' };
  notificationsOverlay: undefined;
  'notifications-setting': undefined;
  'notification-super-changed': { isNowSuper?: boolean; lostSuper?: boolean; keptSuper?: boolean };
  onboardingIntro: { login: boolean } | undefined;
  onboarding: NavigatorScreenParams<onboarding.Parameters> | undefined;
  splash: undefined;
  landing: undefined;
  payment: { promoCode?: string } | undefined;
  'payment-success': undefined;
  quickNew: NavigatorScreenParams<quickNew.Parameters> | undefined;
  loan: NavigatorScreenParams<management.Parameters> | undefined;
  freeSub: {
    userNowContributor: boolean;
    finishedTutorial?: boolean;
    userGotFreeSub?: false | FreeSubSignalData['program'];
    numOffersCreated?: number | false;
  };
  intro: NavigatorScreenParams<introModal.Parameters>;
  trialPromo: undefined;
  tutorialModal: { tutorial: Tutorial };
  'offer-availability': {
    listingId?: Listing['id'];
    goBack?: boolean;
    blockedDates?: string[];
    singleDate?: boolean;
    singleDateType?: 'donation' | 'sell';
  };
  'loan-dates-picker': {
    url: string;
    promptIntroduceYourself: boolean;
    goBackTo: 'one-channel' | 'one-loan';
    loanId?: Loan['id'];
  };
  'loan-fee': { listingId: Listing['id'] };
  refrainOfferAvailabilityPopup: { who: 'lender.before' | 'lender.after' | 'borrower'; listingId?: string };
  listingModal: NavigatorScreenParams<billboard.Parameters>;
  userProfileModal: NavigatorScreenParams<billboard.Parameters>;
  newAddressModal: NavigatorScreenParams<billboard.Parameters>;
  chooseUserType: { userType: string } | undefined;
  publicUserWelcome: undefined;
  presentPartageClub: undefined;
  presentCommunity: undefined;
  'new-post': { loanId: string; listingId: string };
  'new-rating': {
    loanId: string;
    listingId: string;
    channelUrl: string;
    isLender: boolean;
    subjectName: string;
    subjectImage?: string | null;
    initialExperience?: 'positive' | 'negative';
  };
  'rating-thanks': {
    loanId: string;
    listingId: string;
    channelUrl: string;
    isLender: boolean;
    subjectName: string;
    subjectImage?: string | null;
    recommended: boolean;
    initialExperience?: 'positive' | 'negative';
    overallRating?: number;
    assetQualityRating?: number | null;
    logisticsReliabilityRating?: number | null;
    communityTrustRating?: number | null;
    comment?: string | null;
  };
  'update-rating': {
    loanId: string;
    listingId: string;
    channelUrl: string;
    isLender: boolean;
    subjectName: string;
    subjectImage?: string | null;
    initialExperience?: 'positive' | 'negative';
    overallRating?: number;
    assetQualityRating?: number | null;
    logisticsReliabilityRating?: number | null;
    communityTrustRating?: number | null;
    comment?: string | null;
  };
  'edit-post': { postId: string; listingId: string };
  'post-comments': { postId: string };
  streak: undefined;
  'streak-members': { period: string };
  help: undefined;
  'chat-how-it-works': { isLender: boolean };
  'one-loan': { id: Loan['id']; listingId?: Listing['id'] };
};

export { linking } from './root-stack.linking';

// const Stack = isLargeUI ? createStackNavigator<Parameters>() : createNativeStackNavigator<Parameters>();
const Stack = createDefaultNativeStackNavigator<Parameters>();
const isListingModalMountedAtom = atom(false);

function isListingOpenedFromMap(params: Parameters['listingModal']) {
  if (params.screen !== 'one-listing') return false;
  return params.params.hideApproximateLocation === true;
}

const ListingModalScreen: NavigationScreen<'listingModal'> = ({ navigation, route }) => {
  const { isLargeUI } = useUISize();
  const setListingModalMounted = useSetAtom(isListingModalMountedAtom);
  const isOpenedFromMap = isListingOpenedFromMap(route.params);

  useLayoutEffect(() => {
    setListingModalMounted(!isLargeUI && isOpenedFromMap);
    return () => setListingModalMounted(false);
  }, [isLargeUI, isOpenedFromMap, setListingModalMounted]);

  if (isLargeUI || !isOpenedFromMap) {
    return <billboard.ListingNavigator />;
  }

  return (
    <View style={styles.listingModalOverlay}>
      <Pressable accessibilityRole="button" onPress={navigation.goBack} style={StyleSheet.absoluteFill} />
      <View style={styles.listingModalSheet}>
        <billboard.ListingNavigator />
      </View>
      <InsuredUserDetails />
    </View>
  );
};

function RootInsuredUserDetails() {
  const isListingModalMounted = useAtomValue(isListingModalMountedAtom);
  const routePath = useSyncExternalStore(subscribeNavigationState, getCurrentRoutePath, () => undefined);
  return isListingModalMounted || routePath === 'presentCommunity' ? null : <InsuredUserDetails />;
}

function UnauthenticatedWebRouteGuard() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const routePath = useSyncExternalStore(subscribeNavigationState, getCurrentRoutePath, () => undefined);
  const isNavigationReady = useSyncExternalStore(
    subscribeNavigationState,
    () => navigationRef.isReady(),
    () => false,
  );

  useEffect(() => {
    if (Platform.OS !== 'web' || isLoggedIn || isStarting || !isNavigationReady) return;
    const guestAccessEnabled = isGuestAccessEnabled();
    const redirect = getUnauthenticatedWebRedirect(routePath, guestAccessEnabled);
    if (!redirect) return;

    if (redirect === 'guest-billboard') {
      navigationRef.resetRoot({
        index: 0,
        routes: [
          {
            name: 'main',
            state: {
              index: 0,
              routes: [
                {
                  name: 'billboard',
                  state: { index: 0, routes: [{ name: 'all-listings' }] },
                },
              ],
            },
          },
        ],
      });
      return;
    }

    navigationRef.resetRoot({ index: 0, routes: [{ name: 'onboardingIntro' }] });
  }, [isLoggedIn, isNavigationReady, isStarting, routePath]);

  return null;
}

function getPublicScreens(isLargeUI: boolean) {
  return (
    <>
      <Stack.Screen component={SplashScreen} name="splash" />
      <Stack.Screen
        component={PresentPartageClubScreen}
        name="presentPartageClub"
        options={{ animationTypeForReplace: 'push' }}
      />
      <Stack.Screen
        component={PresentCommunityScreen}
        name="presentCommunity"
        options={
          isLargeUI
            ? {
                animationTypeForReplace: 'push',
                presentation: 'transparentModal',
                animation: 'fade',
                contentStyle: { backgroundColor: 'transparent' },
              }
            : { animationTypeForReplace: 'push' }
        }
      />
      <Stack.Screen
        component={PaymentSuccessScreen}
        name="payment-success"
        options={
          isLargeUI
            ? {
                animationTypeForReplace: 'push',
                presentation: 'transparentModal',
                animation: 'fade',
                contentStyle: { backgroundColor: 'transparent' },
              }
            : { animationTypeForReplace: 'push' }
        }
      />
      <Stack.Screen component={main.Navigator} name="main" />

      <Stack.Group screenOptions={() => ({ presentation: 'fullScreenModal', animation: 'slide_from_bottom' })}>
        <Stack.Screen
          component={ChooseUserTypeScreen}
          name="chooseUserType"
          options={({ navigation, route }) => createModalScreenOptions(navigation, route, undefined, false)}
        />
        <Stack.Screen
          component={PublicUserWelcomeScreen}
          name="publicUserWelcome"
          options={({ navigation, route }) => createModalScreenOptions(navigation, route, undefined, false)}
        />
        <Stack.Screen
          component={IntroScreen}
          name="onboardingIntro"
          options={({ navigation, route }) =>
            isLargeUI ? {} : createModalScreenOptions(navigation, route, undefined, false)
          }
        />
      </Stack.Group>

      <Stack.Group screenOptions={{ presentation: 'modal', animation: 'slide_from_bottom' }}>
        <Stack.Screen component={onboarding.Navigator} name="onboarding" />
      </Stack.Group>

      <Stack.Group
        screenOptions={
          isLargeUI
            ? {
                animationTypeForReplace: 'push',
                presentation: 'transparentModal',
                animation: 'fade',
                contentStyle: { backgroundColor: 'transparent' },
              }
            : { presentation: 'modal', animation: 'slide_from_bottom' }
        }
      >
        <Stack.Screen
          component={passwordRecovery.Navigator}
          name="password-recovery"
          options={{ animationTypeForReplace: 'push' }}
        />
        <Stack.Screen component={ServiceCategoriesScreen} name="serviceCategories" />
        <Stack.Screen
          component={EditListingScreen}
          name="edit-listing"
          options={({ navigation, route }) =>
            isLargeUI
              ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>)
              : createModalScreenOptions(navigation, route)
          }
        />
        <Stack.Screen
          component={PaywallScreen}
          name="paywall"
          options={({ navigation, route }) =>
            isLargeUI
              ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>)
              : createModalScreenOptions(navigation, route, undefined, false)
          }
        />
        <Stack.Screen component={MyAddressScreen} name="my-address" />
        <Stack.Screen
          component={CategoriesScreen}
          name="categories"
          options={({ navigation, route }) =>
            isLargeUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>) : {}
          }
        />
        <Stack.Screen
          component={TagsScreen}
          name="tags"
          options={({ navigation, route }) =>
            isLargeUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>) : {}
          }
        />
        <Stack.Screen
          component={HelpScreen}
          name="help"
          options={({ navigation, route }) => {
            if (isLargeUI) {
              return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
            }
            return createModalScreenOptions(navigation, route, undefined, false);
          }}
        />
      </Stack.Group>
    </>
  );
}

function getAuthenticatedSecondaryScreens(isLargeUI: boolean) {
  return (
    <>
      <Stack.Screen
        component={introModal.Navigator}
        name="intro"
        options={
          isLargeUI
            ? {
                animationTypeForReplace: 'push',
                presentation: 'transparentModal',
                animation: 'fade',
                contentStyle: {
                  backgroundColor: 'transparent',
                },
              }
            : {
                animationTypeForReplace: 'push',
              }
        }
      />
      <Stack.Screen
        component={TrialPromoScreen}
        name="trialPromo"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={TutorialModalScreen}
        name="tutorialModal"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={HowItWorksScreen}
        name="chat-how-it-works"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, undefined, false);
        }}
      />
      <Stack.Screen
        component={FreeSubScreen}
        name="freeSub"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={billboard.UserProfileNavigator}
        name="userProfileModal"
        options={
          isLargeUI
            ? {
                animationTypeForReplace: 'push',
                presentation: 'transparentModal',
                animation: 'fade',
                contentStyle: {
                  backgroundColor: 'transparent',
                },
              }
            : {
                animationTypeForReplace: 'push',
              }
        }
      />
      <Stack.Screen
        component={ListingModalScreen}
        name="listingModal"
        options={({ route }) => {
          if (isLargeUI) {
            return {
              animationTypeForReplace: 'push',
              presentation: 'transparentModal',
              animation: 'fade',
              contentStyle: {
                backgroundColor: 'transparent',
              },
            };
          }

          if (isListingOpenedFromMap(route.params)) {
            return {
              animationTypeForReplace: 'push',
              animation: 'slide_from_bottom',
              sheetAllowedDetents: [0.98],
              contentStyle: {
                backgroundColor: 'transparent',
              },
              presentation: 'transparentModal',
            };
          }

          return {
            animationTypeForReplace: 'push',
          };
        }}
      />

      <Stack.Screen
        component={billboard.NewAddressNavigator}
        name="newAddressModal"
        options={{
          animationTypeForReplace: 'push',
        }}
      />
      <Stack.Screen
        component={NewRatingScreen}
        name="new-rating"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={RatingThanksScreen}
        name="rating-thanks"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={UpdateRatingScreen}
        name="update-rating"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={NewPostScreen}
        name="new-post"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={EditPostScreen}
        name="edit-post"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={PostCommentsScreen}
        name="post-comments"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
    </>
  );
}

function getAuthenticatedPrimaryRoutes(isLargeUI: boolean) {
  return (
    <>
      <Stack.Screen component={chat.Navigator} name="chat" />
      <Stack.Screen
        component={InviteScreen}
        name="invite"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, undefined, false);
        }}
      />
      <Stack.Screen
        component={StreakScreenStandalone}
        name="streak"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, undefined, false);
        }}
      />
      <Stack.Screen
        component={StreakMembersScreen}
        name="streak-members"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, undefined, false);
        }}
      />
      <Stack.Screen
        component={NewScreen}
        name="new"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={BulkNewScreen}
        name="bulkNew"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={OfferHubScreen}
        name="offer-hub"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen component={NotificationExplainerScreen} name="notification-explainer" />
      <Stack.Screen
        component={NotificationsOverlayScreen}
        name="notificationsOverlay"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebRightPanelScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, undefined, false);
        }}
      />
      <Stack.Screen
        component={NotificationsSettingScreen}
        name="notifications-setting"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={NotificationSuperChangedScreen}
        name="notification-super-changed"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={PaymentScreen}
        name="payment"
        options={({ navigation, route }) =>
          isLargeUI
            ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>)
            : createModalScreenOptions(navigation, route)
        }
      />
      <Stack.Screen
        component={quickNew.Navigator}
        name="quickNew"
        options={{
          animationTypeForReplace: 'push',
        }}
      />
      <Stack.Screen
        component={management.NavigatorLoan}
        name="loan"
        options={{
          animationTypeForReplace: 'push',
        }}
      />
      <Stack.Screen
        component={OfferAvailabilityScreen}
        name="offer-availability"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={LoanDatesPickerScreen}
        name="loan-dates-picker"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return {};
        }}
      />
    </>
  );
}

function getAuthenticatedSecondaryRoutes(isLargeUI: boolean) {
  return (
    <>
      <Stack.Screen
        component={LoanFeeScreen}
        name="loan-fee"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={LoanDetailsScreen}
        name="one-loan"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={RefrainOfferAvailabilityPopupScreen}
        name="refrainOfferAvailabilityPopup"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      {getAuthenticatedSecondaryScreens(isLargeUI)}
    </>
  );
}

function getAuthenticatedRoutes(isLargeUI: boolean) {
  return (
    <Stack.Group
      screenOptions={
        isLargeUI
          ? {
              animationTypeForReplace: 'push',
              presentation: 'transparentModal',
              animation: 'fade',
              contentStyle: {
                backgroundColor: 'transparent',
              },
            }
          : {
              animationTypeForReplace: 'push',
              headerLeft: undefined,
            }
      }
    >
      {getAuthenticatedPrimaryRoutes(isLargeUI)}
      {getAuthenticatedSecondaryRoutes(isLargeUI)}
    </Stack.Group>
  );
}

export function Navigator() {
  const { isLargeUI } = useUISize();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const paywallVisible = useAtomValue(presentPayWallAtom);
  const hasNavigatedToPaywall = useRef(false);

  useEffect(() => {
    if (paywallVisible && !hasNavigatedToPaywall.current && navigationRef.isReady()) {
      hasNavigatedToPaywall.current = true;
      navigationRef.navigate('paywall', undefined, { pop: true });
    }
    if (!paywallVisible) {
      hasNavigatedToPaywall.current = false;
    }
  }, [paywallVisible]);

  return (
    <FullWidthContainerView>
      <UnauthenticatedWebRouteGuard />
      <NotificationHandler />
      <NotificationWarnModal />

      <Stack.Navigator screenOptions={{ headerShown: false }} initialRouteName="splash">
        {getPublicScreens(isLargeUI)}
        {isLoggedIn || isStarting ? getAuthenticatedRoutes(isLargeUI) : null}
      </Stack.Navigator>
      <RootInsuredUserDetails />
    </FullWidthContainerView>
  );
}

const styles = StyleSheet.create({
  listingModalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.18)',
    flex: 1,
    justifyContent: 'flex-end',
  },
  listingModalSheet: {
    backgroundColor: 'white',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    height: '92%',
    overflow: 'hidden',
    boxShadow: '0px -8px 24px rgba(0, 0, 0, 0.18)',
  },
});
